const REPORT_WINDOW_MS = 3000;

const state = {
  installed: false,
  lastSignature: '',
  lastTimestamp: 0,
};

function envLabel() {
  const mode = String(import.meta.env.MODE || '').toLowerCase();
  if (mode.includes('prod')) return 'prod';
  if (mode.includes('stag')) return 'staging';
  return 'dev';
}

function projectSlug() {
  const raw = String(import.meta.env.VITE_ERROR_TRACE_PROJECT_SLUG || '').trim();
  return raw || 'weros-webdynamics';
}

function apiBaseUrl() {
  const raw = String(import.meta.env.VITE_ERROR_TRACE_API_BASE || '').trim();
  if (!raw) return '';
  if (raw.endsWith('/')) return raw.slice(0, -1);
  return raw;
}

function ingestToken() {
  return String(import.meta.env.VITE_ERROR_TRACE_PUBLISHABLE_KEY || '').trim();
}

function endpointUrl() {
  const base = apiBaseUrl();
  if (!base) return '/api/error-trace';
  if (base.endsWith('/api')) return `${base}/error-trace`;
  return `${base}/api/error-trace`;
}

function asNonEmptyString(value) {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function asStatus(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  if (!normalized) return undefined;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return undefined;
  return parsed;
}

function truncate(value, max) {
  if (value.length <= max) return value;
  return `${value.slice(0, Math.max(0, max - 3))}...`;
}

function safeJsonStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

function isGenericMessage(value) {
  if (!value) return true;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return true;
  if (
    normalized === '{}' ||
    normalized === '[]' ||
    normalized === 'null' ||
    normalized === 'undefined' ||
    normalized === '"{}"' ||
    normalized === '"[]"' ||
    normalized === 'error' ||
    normalized === 'unknown error' ||
    normalized === 'unhandled rejection' ||
    normalized === 'non-error rejection'
  ) {
    return true;
  }
  return false;
}

function toErrorParts(error) {
  if (error instanceof Error) {
    const codeValue = error.code;
    return {
      message: asNonEmptyString(error.message) || asNonEmptyString(error.name) || null,
      stack: asNonEmptyString(error.stack) || '',
      name: asNonEmptyString(error.name) || undefined,
      code:
        typeof codeValue === 'string' || typeof codeValue === 'number'
          ? String(codeValue)
          : undefined,
      status: asStatus(error.status) ?? asStatus(error.statusCode),
    };
  }
  if (typeof error === 'string') {
    return {
      message: asNonEmptyString(error),
      stack: '',
    };
  }
  if (typeof error === 'number' || typeof error === 'boolean' || typeof error === 'bigint') {
    return {
      message: String(error),
      stack: '',
    };
  }
  if (error && typeof error === 'object') {
    const messageCandidate =
      asNonEmptyString(error.message) ||
      asNonEmptyString(error.detail) ||
      asNonEmptyString(error.error_description) ||
      asNonEmptyString(error.reason) ||
      asNonEmptyString(error.title) ||
      asNonEmptyString(error.statusText);
    const name = asNonEmptyString(error.name) || undefined;
    const code =
      typeof error.code === 'string' || typeof error.code === 'number'
        ? String(error.code)
        : undefined;
    const status =
      asStatus(error.status) ??
      asStatus(error.status_code) ??
      asStatus(error.http_status) ??
      asStatus(error.statusCode);
    const statusText = asNonEmptyString(error.statusText);
    const rawReason = safeJsonStringify(error);
    const reasonPreview = rawReason
      ? truncate(rawReason, 220)
      : asNonEmptyString(Object.prototype.toString.call(error));
    let message = messageCandidate;
    if (!message && status !== undefined) {
      message = statusText ? `HTTP ${status} ${statusText}` : `HTTP ${status}`;
    }
    if (!message && name && !isGenericMessage(name)) {
      message = name;
    }
    if (!message && rawReason && !isGenericMessage(rawReason)) {
      message = truncate(rawReason, 400);
    }
    return {
      message,
      stack: asNonEmptyString(error.stack) || '',
      name,
      code,
      status,
      reason_preview: reasonPreview || undefined,
      raw_reason_json: rawReason ? truncate(rawReason, 1200) : undefined,
    };
  }
  return {
    message: null,
    stack: '',
  };
}

function signatureFor(message, stack, route) {
  return `${message}|${stack || ''}|${route || ''}`;
}

async function sendPayload(payload) {
  const token = ingestToken();
  if (!token) return;

  try {
    await fetch(endpointUrl(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-error-trace-token': token,
        'x-error-trace-origin': window.location.origin,
      },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: 'omit',
    });
  } catch {
  }
}

async function reportClientError(error, extra = {}) {
  const parts = toErrorParts(error);
  const message = parts.message;
  if (!message || isGenericMessage(message)) {
    return;
  }
  const stack = parts.stack;
  const route = window.location.pathname || '/';
  const signature = signatureFor(message, stack, route);
  const now = Date.now();

  if (state.lastSignature === signature && now - state.lastTimestamp < REPORT_WINDOW_MS) {
    return;
  }

  state.lastSignature = signature;
  state.lastTimestamp = now;

  await sendPayload({
    source: 'frontend',
    level: 'error',
    message,
    stack_text: stack || undefined,
    route,
    project_slug: projectSlug(),
    environment: envLabel(),
    http_status: parts.status ?? undefined,
    context: {
      url: window.location.href,
      user_agent: navigator.userAgent,
      error_name: parts.name || undefined,
      error_code: parts.code || undefined,
      error_status: parts.status ?? undefined,
      error_reason_preview: parts.reason_preview || undefined,
      error_raw_reason_json: parts.raw_reason_json || undefined,
      ...extra,
    },
  });
}

export function initErrorTraceHandlers() {
  if (state.installed) return;
  state.installed = true;

  window.addEventListener('error', (event) => {
    reportClientError(event.error || event.message, {
      handler: 'window.error',
      filename: event.filename || null,
      lineno: event.lineno || null,
      colno: event.colno || null,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportClientError(event.reason, {
      handler: 'unhandledrejection',
    });
  });
}
